import { ChatMessage, ReservedMessageIds } from "@/entities/messaging/types";
import { isDateSeparatorMessage } from "@/entities/messaging/helpers";
import { ReservedUserIds } from "@/entities/users/types";

export const LEGACY_TABS_IDS = ["all", "chats", "alerts", "starred"];
export const REPORTS_TABS_IDS = ["chat", "reports", "starred"];

export const ALERT_USER_IDS: Set<string> = new Set([
  ReservedUserIds.ALERTS_CONVERSATIONAL_ID,
  ReservedUserIds.NOTIFICATIONS_CONVERSATIONAL_ID
]);

// DREAMER_CONVERSATIONAL_ID intentionally excluded — shouldShowWaiting must see Dreamer replies to correctly determine response state
export const NON_CHAT_USER_IDS: Set<string> = new Set([
  ...ALERT_USER_IDS,
  ReservedUserIds.MENTOR_CONVERSATIONAL_ID,
  ReservedUserIds.SYSTEM_CONVERSATIONAL_ID
]);

/** Whether a message is a turn of the user-Dreamer conversation. */
export const isChatTurnMessage = (message: ChatMessage): boolean =>
  !!message.user &&
  !isDateSeparatorMessage(message) &&
  !NON_CHAT_USER_IDS.has(message.user.id);

/**
 * Drops each failed-answer card whose next turn is a Dreamer answer to a
 * request: the question was retried and answered, possibly from another
 * surface (Slack, another tab) that never touched this card. A late answer to
 * a stopped request answers no question still waiting, so the card stays.
 */
export const withoutAnsweredErrorCards = (
  messages: ChatMessage[]
): ChatMessage[] => {
  let nextTurn: ChatMessage | undefined;
  const answeredCardIds = new Set<string>();
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (!isChatTurnMessage(message)) continue;
    if (
      message.isError &&
      nextTurn?.requestId &&
      !nextTurn.isError &&
      !nextTurn.isStoppedResponse &&
      nextTurn.user?.id === ReservedUserIds.DREAMER_CONVERSATIONAL_ID
    ) {
      answeredCardIds.add(message.id);
      continue;
    }
    nextTurn = message;
  }
  return answeredCardIds.size === 0
    ? messages
    : messages.filter((message) => !answeredCardIds.has(message.id));
};

/**
 * Drops the failed-answer card answering the given question, if its next turn
 * is one: the question is being retried, so the card no longer stands.
 */
export const withoutFailedAnswerTo = (
  messages: ChatMessage[],
  questionId: string
): ChatMessage[] => {
  const questionIndex = messages.findIndex(
    (message) => message.id === questionId
  );
  if (questionIndex === -1) return messages;
  const answer = messages
    .slice(questionIndex + 1)
    .find((message) => isChatTurnMessage(message));
  return answer?.isError
    ? messages.filter((message) => message.id !== answer.id)
    : messages;
};

/**
 * Whether a message belongs under the given tab. The legacy "chats" tab keeps
 * alerts out; the "chat" tab of the reports tab set shows them in line, since
 * that set has no tab of their own.
 */
export const filterBySlicer = (slicer: string, message: ChatMessage) => {
  if (isDateSeparatorMessage(message)) {
    return true;
  }

  const userId = message.user?.id ?? "";
  switch (slicer) {
    case "all":
      return true;
    case "chats":
      return (
        message.id !== ReservedMessageIds.MSG_SEPARATOR &&
        !NON_CHAT_USER_IDS.has(userId)
      );
    case "chat":
      return (
        message.id !== ReservedMessageIds.MSG_SEPARATOR &&
        (ALERT_USER_IDS.has(userId) || !NON_CHAT_USER_IDS.has(userId))
      );
    case "alerts":
      return ALERT_USER_IDS.has(userId);
    case "starred":
      return message.isStarred;
    default:
      return false;
  }
};

/**
 * The messages a tab renders. Date separators are inserted for every day in the
 * unfiltered feed, so a day whose messages all belong to another tab would keep
 * its separator with nothing under it — those are dropped.
 */
export const filterMessagesBySlicer = (
  slicer: string,
  messages: ChatMessage[]
): ChatMessage[] => {
  const visible = messages.filter((message) => filterBySlicer(slicer, message));
  return visible.filter(
    (message, index) =>
      !isDateSeparatorMessage(message) ||
      (index + 1 < visible.length && !isDateSeparatorMessage(visible[index + 1]))
  );
};
